import ReactDOM from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { Provider as RollbarProvider, ErrorBoundary } from '@rollbar/react';
import { rollbar, rollbarConfig } from './lib/rollbar';
import './lib/mixpanel';
import { AuthProvider } from './hooks/use-auth';
import { ToastProvider } from './contexts/toast-context';

import { env } from './utils/env';
import { routeTree } from './routeTree.gen';
import './styles/styles.scss';
import '../index.css';
import { StrictMode } from 'react';

if (env?.VITE_NODE_ENV && env.VITE_NODE_ENV !== 'production') {
  document.title = `[${env.VITE_NODE_ENV.toUpperCase()}] Qluu`;
}

const router = createRouter({
  routeTree,
  scrollRestoration: true,
});

// Capture beforeinstallprompt before React mounts so the hook doesn't miss it
window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault();
  (window as any).__pwaInstallPrompt = e;
});

// Listen for navigation requests from service worker (notification clicks)
if ('serviceWorker' in navigator) {
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'NOTIFICATION_CLICK' && event.data?.url) {
      router.navigate({ to: event.data.url });
    }
  });

  // When a new service worker takes over, reload the page so the browser
  // loads fresh assets from the new deployment instead of the stale cached ones.
  // Guard with hadController so we don't reload on the very first SW installation.
  const hadController = !!navigator.serviceWorker.controller;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (hadController) window.location.reload();
  });
}

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

// Render the app
const rootElement = document.getElementById('root')!;
const root = ReactDOM.createRoot(rootElement);
const app = (
  <StrictMode>
    <ToastProvider>
      <AuthProvider>
        <RouterProvider router={router} />
      </AuthProvider>
    </ToastProvider>
  </StrictMode>
);

// Wrap the app in Rollbar only when its enabled, otherwise render app directly
root.render(
  rollbarConfig.enabled ? (
    <RollbarProvider instance={rollbar}>
      <ErrorBoundary>{app}</ErrorBoundary>
    </RollbarProvider>
  ) : (
    app
  ),
);
